import { PlusCircleIcon } from '@heroicons/react/24/outline';
import {
  Box,
  Flex,
  List,
  ListItem,
  RadioGroup,
  Space,
  Stack,
  Text,
  Title
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useIntl } from 'react-intl';

import { PageLoading } from '@/pages/PageLoading';
import { usePaymentFrequencyList } from '@/pages/club/onboard/components/constants/paymentsFrequencyList';
import {
  consummationItem,
  crazyMenuItem
} from '@/pages/club/onboard/components/stages/payments/constants/schema';
import { usePaymentsPage } from '@/pages/club/onboard/components/stages/payments/hooks/usePaymentsPage';
import { translate } from '@/shared/i18n/translate';
import { LBModalFullscreenLayout } from '@/shared/layouts';
import {
  LBButton,
  LBCheckbox,
  LBLinkButton,
  LBListItem,
  LBModalHeader,
  LBNumberCurrencyInput,
  LBNumberInput,
  LBRadio,
  LBSelect,
  LBTextInput
} from '@/shared/ui-kit/components';

export const Payments = () => {
  const {
    state: { isLoading, form, profileCurrency },
    functions: { onSubmit }
  } = usePaymentsPage();

  const { formatMessage } = useIntl();

  const paymentFrequencyList = usePaymentFrequencyList();

  const [
    consumationModalStatus,
    { open: consumationModalOpen, close: consumationModalClose }
  ] = useDisclosure();
  const [
    crazyMenuModalStatus,
    { open: crazyMenuModalOpen, close: crazyMenuModalClose }
  ] = useDisclosure();

  const isValidCrazyMenuItems = form
    .getValues()
    .data.crazyMenu.every((item) => crazyMenuItem.safeParse(item).success);

  const isValidConsumationItems = form
    .getValues()
    .data.consummationItems.every(
      (item) => consummationItem.safeParse(item).success
    );

  if (isLoading) return <PageLoading />;

  return (
    <>
      <LBModalFullscreenLayout
        opened={consumationModalStatus}
        onClose={consumationModalClose}
      >
        <LBModalHeader
          onClose={isValidConsumationItems ? consumationModalClose : () => {}}
        />
        <Title>{translate('popups.consumation.title')}</Title>
        <Space h={16} />
        <LBSelect
          key={form.key('data.consummationFrequency')}
          {...form.getInputProps('data.consummationFrequency')}
          data={paymentFrequencyList}
          label={translate('form.paymentFrequency.label')}
          maw={124}
        />
        <Space h={24} />
        <Text fz={14}>{translate('popups.consumation.description')}</Text>
        <Space h={16} />
        <Stack gap={16}>
          {form.getValues().data.consummationItems.map((_item, index) => (
            <Flex key={`consumation-${index}`} direction='column' gap={8}>
              <LBTextInput
                key={form.key(`data.consummationItems.${index}.name`)}
                label={`${index + 1}.`}
                placeholder={formatMessage({ id: 'form.positionItem.label' })}
                error={form.validateField(
                  `data.consummationItems.${index}.name`
                )}
                {...form.getInputProps(`data.consummationItems.${index}.name`)}
              />
              <Flex align='start' gap={16} justify='space-between'>
                <LBNumberCurrencyInput
                  key={form.key(`data.consummationItems.${index}.price`)}
                  currencySymbol={profileCurrency}
                  label={translate('form.price.label')}
                  placeholder='0'
                  error={form.validateField(
                    `data.consummationItems.${index}.price`
                  )}
                  {...form.getInputProps(
                    `data.consummationItems.${index}.price`
                  )}
                />
                <LBNumberCurrencyInput
                  key={form.key(`data.consummationItems.${index}.reward`)}
                  currencySymbol={profileCurrency}
                  label={translate('form.priceForArtist.label')}
                  placeholder='0'
                  error={form.validateField(
                    `data.consummationItems.${index}.reward`
                  )}
                  {...form.getInputProps(
                    `data.consummationItems.${index}.reward`
                  )}
                />
              </Flex>
              <Flex justify='end'>
                <LBLinkButton
                  onClick={() =>
                    form.removeListItem('data.consummationItems', index)
                  }
                >
                  {translate('commons.delete')}
                </LBLinkButton>
              </Flex>
            </Flex>
          ))}
        </Stack>
        <Space h={18} />
        {form.getValues().data.consummationItems.length !== 5 && (
          <LBLinkButton
            leftsection={<PlusCircleIcon width={18} />}
            onClick={() =>
              form.insertListItem('data.consummationItems', {
                name: '',
                price: ''
              })
            }
          >
            {translate('commons.add')}
          </LBLinkButton>
        )}
        <Space h={32} />
        <LBButton
          fullWidth
          disabled={!isValidConsumationItems}
          onClick={consumationModalClose}
        >
          {translate('commons.save')}
        </LBButton>
      </LBModalFullscreenLayout>
      <LBModalFullscreenLayout
        opened={crazyMenuModalStatus}
        onClose={crazyMenuModalClose}
      >
        <LBModalHeader
          onClose={isValidCrazyMenuItems ? crazyMenuModalClose : () => {}}
        />
        <Title>{translate('popups.crazyMenu.title')}</Title>
        <Space h={16} />
        <Flex align='center' gap={16} justify='space-between'>
          <LBSelect
            key={form.key('data.crazyMenuPaymentFrequency')}
            {...form.getInputProps('data.crazyMenuPaymentFrequency')}
            data={paymentFrequencyList}
            label={translate('form.paymentFrequency.label')}
          />
          <LBNumberCurrencyInput
            key={form.key('data.crazyMenuPercent')}
            {...form.getInputProps('data.crazyMenuPercent')}
            currencySymbol='%'
            label={translate('form.artistPercent.label')}
            placeholder='0'
          />
        </Flex>
        <Space h={24} />
        <Text fz={14}>{translate('popups.crazyMenu.description')}</Text>
        <Space h={16} />
        <Stack gap={8}>
          {form.getValues().data.crazyMenu.map((_item, index) => (
            <Flex key={`crazy-${index}`} direction='column' gap={8}>
              <LBTextInput
                key={form.key(`data.crazyMenu.${index}.name`)}
                error={form.validateField(`data.crazyMenu.${index}.name`)}
                label={`${index + 1}.`}
                placeholder={formatMessage({ id: 'form.positionItem.label' })}
                {...form.getInputProps(`data.crazyMenu.${index}.name`)}
              />
              <Flex align='center' gap={16} justify='space-between'>
                <LBNumberCurrencyInput
                  key={form.key(`data.crazyMenu.${index}.price`)}
                  currencySymbol={profileCurrency}
                  error={form.validateField(`data.crazyMenu.${index}.price`)}
                  label={translate('form.price.label')}
                  placeholder='0'
                  {...form.getInputProps(`data.crazyMenu.${index}.price`)}
                />
              </Flex>
              <Flex justify='end'>
                <LBLinkButton
                  onClick={() => form.removeListItem('data.crazyMenu', index)}
                >
                  {translate('commons.delete')}
                </LBLinkButton>
              </Flex>
            </Flex>
          ))}
        </Stack>
        <Space h={18} />
        {form.getValues().data.crazyMenu.length !== 5 && (
          <LBLinkButton
            leftsection={<PlusCircleIcon width={18} />}
            onClick={() =>
              form.insertListItem('data.crazyMenu', {
                name: '',
                price: ''
              })
            }
          >
            {translate('commons.add')}
          </LBLinkButton>
        )}
        <Space h={32} />
        <LBButton
          fullWidth
          disabled={!isValidCrazyMenuItems}
          onClick={crazyMenuModalClose}
        >
          {translate('commons.save')}
        </LBButton>
      </LBModalFullscreenLayout>
      <form onSubmit={onSubmit}>
        <Title order={1}>
          {translate('pages.onboarding.club.payments.title')}
        </Title>
        <Space h={32} />
        <Stack gap={24}>
          <Box>
            <LBCheckbox
              key={form.key('additional.clubFee')}
              disabled={form.submitting}
              label={translate('commons.clubFee')}
              {...form.getInputProps('additional.clubFee', {
                type: 'checkbox'
              })}
            />
            <Space h={8} />
            <List withPadding listStyleType='none'>
              <ListItem>
                <Flex align='center' gap={16} justify='space-between'>
                  <LBNumberCurrencyInput
                    key={form.key('data.clubFee')}
                    currencySymbol={profileCurrency}
                    placeholder='0'
                    disabled={
                      !form.getValues().additional.clubFee || form.submitting
                    }
                    {...form.getInputProps('data.clubFee')}
                  />
                  <LBSelect
                    key={form.key('data.clubFreeFrequency')}
                    allowDeselect={false}
                    data={paymentFrequencyList}
                    disabled={
                      !form.getValues().additional.clubFee || form.submitting
                    }
                    {...form.getInputProps('data.clubFreeFrequency')}
                  />
                </Flex>
              </ListItem>
            </List>
          </Box>
          <Box>
            <Title order={3}>{translate('commons.forShift')}</Title>
            <Space h={24} />
            <RadioGroup
              key={form.getValues().additional.forShift}
              {...form.getInputProps('additional.forShift')}
            >
              <Stack gap={24}>
                <Box>
                  <LBRadio
                    label={translate('commons.general')}
                    value='Common'
                    disabled={
                      form.getValues().additional.clubFee || form.submitting
                    }
                  />
                  <Space h={16} />
                  <List withPadding listStyleType='none'>
                    <RadioGroup
                      key={form.getValues().additional.forShiftCommon}
                      {...form.getInputProps('additional.forShiftCommon')}
                    >
                      <Stack gap={8}>
                        <ListItem>
                          <LBRadio
                            label={translate('commons.rate')}
                            value='Bid'
                            description={translate(
                              'pages.onboarding.club.payments.rateFieldDescription'
                            )}
                            disabled={
                              form.getValues().additional.clubFee ||
                              form.getValues().additional.forShift !==
                                'Common' ||
                              form.submitting
                            }
                          />
                          <List withPadding listStyleType='none'>
                            <ListItem>
                              <Stack gap={8}>
                                <Flex
                                  align='center'
                                  gap={16}
                                  justify='space-between'
                                >
                                  <LBNumberCurrencyInput
                                    key={form.key('data.dailyRateMin')}
                                    currencySymbol={profileCurrency}
                                    disabled={
                                      form.getValues().additional.clubFee ||
                                      form.getValues().additional
                                        .forShiftCommon !== 'Bid' ||
                                      form.getValues().additional.forShift !==
                                        'Common' ||
                                      form.submitting
                                    }
                                    {...form.getInputProps('data.dailyRateMin')}
                                  />
                                  <LBSelect
                                    allowDeselect={false}
                                    data={paymentFrequencyList}
                                    {...form.getInputProps(
                                      'data.dailyRatePaymentFrequency'
                                    )}
                                    key={form.key(
                                      'data.dailyRatePaymentFrequency'
                                    )}
                                    maw={124}
                                    disabled={
                                      form.getValues().additional.clubFee ||
                                      form.getValues().additional
                                        .forShiftCommon !== 'Bid' ||
                                      form.getValues().additional.forShift !==
                                        'Common' ||
                                      form.submitting
                                    }
                                  />
                                </Flex>
                              </Stack>
                            </ListItem>
                          </List>
                        </ListItem>
                        <ListItem>
                          <LBRadio
                            label={translate('commons.guarantee')}
                            value='Guarantee'
                            disabled={
                              form.getValues().additional.clubFee ||
                              form.getValues().additional.forShift !==
                                'Common' ||
                              form.submitting
                            }
                          />
                          <Space h={8} />
                          <List withPadding listStyleType='none'>
                            <ListItem>
                              <Stack gap={8}>
                                <Flex
                                  align='center'
                                  gap={16}
                                  justify='space-between'
                                >
                                  <LBNumberCurrencyInput
                                    key={form.key('data.guarantee')}
                                    currencySymbol={profileCurrency}
                                    disabled={
                                      form.getValues().additional.clubFee ||
                                      form.getValues().additional
                                        .forShiftCommon !== 'Guarantee' ||
                                      form.getValues().additional.forShift !==
                                        'Common' ||
                                      form.submitting
                                    }
                                    {...form.getInputProps('data.guarantee')}
                                  />
                                  <LBSelect
                                    key={form.key(
                                      'data.guaranteePaymentFrequency'
                                    )}
                                    allowDeselect={false}
                                    data={paymentFrequencyList}
                                    maw={124}
                                    disabled={
                                      form.getValues().additional.clubFee ||
                                      form.getValues().additional
                                        .forShiftCommon !== 'Guarantee' ||
                                      form.getValues().additional.forShift !==
                                        'Common' ||
                                      form.submitting
                                    }
                                    {...form.getInputProps(
                                      'data.guaranteePaymentFrequency'
                                    )}
                                  />
                                </Flex>
                              </Stack>
                            </ListItem>
                          </List>
                        </ListItem>
                      </Stack>
                    </RadioGroup>
                  </List>
                </Box>
                <Box>
                  <LBRadio
                    label={translate('commons.separated')}
                    value='Separated'
                    disabled={
                      form.getValues().additional.clubFee || form.submitting
                    }
                  />
                  <Space h={4} />
                  <List withPadding listStyleType='none'>
                    <Stack gap={8}>
                      <ListItem>
                        <Flex direction='column' gap={4}>
                          <Text fz={14}>{translate('commons.full')}</Text>

                          <Flex align='center' gap={18}>
                            <LBNumberCurrencyInput
                              currencySymbol={profileCurrency}
                              disabled={
                                form.getValues().additional.clubFee ||
                                form.getValues().additional.forShift !==
                                  'Separated' ||
                                form.submitting
                              }
                              {...form.getInputProps('data.full')}
                              key={form.key('data.full')}
                            />
                            <LBSelect
                              allowDeselect={false}
                              data={paymentFrequencyList}
                              disabled={
                                form.getValues().additional.clubFee ||
                                form.getValues().additional.forShift !==
                                  'Separated' ||
                                form.submitting
                              }
                              {...form.getInputProps(
                                'data.fullPaymentFrequency'
                              )}
                              key={form.key('data.fullPaymentFrequency')}
                            />
                          </Flex>
                        </Flex>
                      </ListItem>
                      <ListItem>
                        <Flex direction='column' gap={4}>
                          <Text fz={14}>{translate('commons.topless')}</Text>

                          <Flex align='center' gap={18}>
                            <LBNumberCurrencyInput
                              currencySymbol={profileCurrency}
                              disabled={
                                form.getValues().additional.clubFee ||
                                form.getValues().additional.forShift !==
                                  'Separated' ||
                                form.submitting
                              }
                              {...form.getInputProps('data.topless')}
                              key={form.key('data.topless')}
                            />
                            <LBSelect
                              allowDeselect={false}
                              data={paymentFrequencyList}
                              disabled={
                                form.getValues().additional.clubFee ||
                                form.getValues().additional.forShift !==
                                  'Separated' ||
                                form.submitting
                              }
                              {...form.getInputProps(
                                'data.toplessPaymentFrequency'
                              )}
                              key={form.key('data.toplessPaymentFrequency')}
                            />
                          </Flex>
                        </Flex>
                      </ListItem>
                      <ListItem>
                        <Flex direction='column' gap={4}>
                          <Text fz={14}>
                            {translate('commons.artistJobs.GoGo')}
                          </Text>

                          <Flex align='center' gap={18}>
                            <LBNumberCurrencyInput
                              currencySymbol={profileCurrency}
                              disabled={
                                form.getValues().additional.clubFee ||
                                form.getValues().additional.forShift !==
                                  'Separated' ||
                                form.submitting
                              }
                              {...form.getInputProps('data.goGo')}
                              key={form.key('data.goGo')}
                            />
                            <LBSelect
                              key={form.key('data.goGoPaymentFrequency')}
                              allowDeselect={false}
                              data={paymentFrequencyList}
                              {...form.getInputProps(
                                'data.goGoPaymentFrequency'
                              )}
                              disabled={
                                form.getValues().additional.clubFee ||
                                form.getValues().additional.forShift !==
                                  'Separated' ||
                                form.submitting
                              }
                            />
                          </Flex>
                        </Flex>
                      </ListItem>
                    </Stack>
                  </List>
                </Box>
                <Flex align='center' justify='space-between'>
                  <LBCheckbox
                    key={form.key('additional.deposit')}
                    label={translate('commons.deposit')}
                    disabled={
                      form.getValues().additional.clubFee || form.submitting
                    }
                    {...form.getInputProps('additional.deposit', {
                      type: 'checkbox'
                    })}
                  />
                  <LBNumberCurrencyInput
                    key={form.key('data.deposit')}
                    currencySymbol={profileCurrency}
                    maw={124}
                    placeholder='0'
                    disabled={
                      form.getValues().additional.clubFee ||
                      !form.getValues().additional.deposit ||
                      form.submitting
                    }
                    {...form.getInputProps('data.deposit')}
                  />
                </Flex>
              </Stack>
            </RadioGroup>
          </Box>
        </Stack>
        <Space h={24} />
        <Box>
          <Title order={3}>{translate('commons.other')}</Title>
          <Space h={24} />
          <Stack gap={16}>
            <Box>
              <LBCheckbox
                key={form.key('additional.show')}
                disabled={form.submitting}
                label={translate('commons.show')}
                {...form.getInputProps('additional.show', { type: 'checkbox' })}
              />
              <Space h={8} />
              <List withPadding listStyleType='none'>
                <ListItem>
                  <Flex align='center' gap={16} justify='space-between'>
                    <LBNumberCurrencyInput
                      key={form.key('data.show')}
                      currencySymbol={profileCurrency}
                      placeholder='0'
                      disabled={
                        !form.getValues().additional.show || form.submitting
                      }
                      {...form.getInputProps('data.show')}
                    />
                    <LBSelect
                      key={form.key('data.showPaymentFrequency')}
                      allowDeselect={false}
                      data={paymentFrequencyList}
                      {...form.getInputProps('data.showPaymentFrequency')}
                      disabled={
                        !form.getValues().additional.show || form.submitting
                      }
                    />
                  </Flex>
                </ListItem>
              </List>
            </Box>
            <Box>
              <LBCheckbox
                key={form.key('additional.tips')}
                disabled={form.submitting}
                label={translate('commons.tea')}
                {...form.getInputProps('additional.tips', { type: 'checkbox' })}
              />
              <Space h={8} />
              <List withPadding listStyleType='none'>
                <ListItem>
                  <Flex align='center' gap={16} justify='space-between'>
                    <LBNumberInput
                      key={form.key('data.tips')}
                      placeholder='0'
                      rightSection={<Text>%</Text>}
                      disabled={
                        !form.getValues().additional.tips || form.submitting
                      }
                      {...form.getInputProps('data.tips')}
                    />
                    {/* <LBSelect
                      key={form.key('data.tipsPaymentFrequency')}
                      allowDeselect={false}
                      data={paymentFrequencyList}
                      {...form.getInputProps('data.tipsPaymentFrequency')}
                      disabled={
                        !form.getValues().additional.tips || form.submitting
                      }
                    /> */}
                  </Flex>
                </ListItem>
              </List>
            </Box>
            <Box>
              <LBCheckbox
                key={form.key('additional.crazyMenu')}
                disabled={form.submitting}
                label={translate('commons.crazyMenu')}
                {...form.getInputProps('additional.crazyMenu', {
                  type: 'checkbox'
                })}
              />
              <Space h={8} />
              <List withPadding listStyleType='none'>
                {form.getValues().additional.crazyMenu && (
                  <Stack gap={4}>
                    {form.getValues().data.crazyMenu.map((item, index) => (
                      <LBListItem key={`crazyMenu-${index}`}>
                        <Flex>
                          <Text fz={14}>{item.name}</Text>
                        </Flex>
                      </LBListItem>
                    ))}
                  </Stack>
                )}
                <Space h={4} />
                <LBLinkButton
                  leftsection={<PlusCircleIcon width={18} />}
                  type='button'
                  disabled={
                    !form.getValues().additional.crazyMenu || form.submitting
                  }
                  onClick={crazyMenuModalOpen}
                >
                  {form.getValues().data.crazyMenu.length
                    ? translate('commons.edit')
                    : translate('commons.add')}
                </LBLinkButton>
              </List>
            </Box>
            <Box>
              <LBCheckbox
                key={form.key('additional.consummationItems')}
                disabled={form.submitting}
                label={translate('commons.artistConsumation.label')}
                {...form.getInputProps('additional.consummationItems', {
                  type: 'checkbox'
                })}
              />
              <Space h={8} />
              <List withPadding listStyleType='none'>
                {form.getValues().additional.consummationItems && (
                  <Stack gap={4}>
                    {form
                      .getValues()
                      .data.consummationItems.map((item, index) => (
                        <LBListItem key={`consummationItems-${index}`}>
                          <Flex>
                            <Text fz={14}>{item.name}</Text>
                          </Flex>
                        </LBListItem>
                      ))}
                  </Stack>
                )}
                <Space h={4} />
                <LBLinkButton
                  leftsection={<PlusCircleIcon width={18} />}
                  type='button'
                  disabled={
                    !form.getValues().additional.consummationItems ||
                    form.submitting
                  }
                  onClick={consumationModalOpen}
                >
                  {form.getValues().data.consummationItems.length
                    ? translate('commons.edit')
                    : translate('commons.add')}
                </LBLinkButton>
              </List>
            </Box>
          </Stack>
        </Box>
        <Space h={32} />
        <LBButton fullWidth loading={form.submitting} type='submit'>
          {translate('commons.finish')}
        </LBButton>
      </form>
    </>
  );
};
